import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  Alert, Button, CustomInput, FormGroup, Input, Label
} from 'reactstrap';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import AdminGuideLink from '../../shared/AdminGuideLink';
import {
  INSERTABLE_TEMPLATE_TOKENS,
  TEMPLATE_METADATA,
  applyTemplatePreviewTokens,
  htmlToPlainText
} from '../../utils/emailTemplatePreview';

interface EmailTemplate {
  id: number;
  templateKey: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  previewText: string;
  enabled: boolean;
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  canManage: boolean;
}

interface LocalState {
  templates: EmailTemplate[];
  selectedKey: string;
  draft: EmailTemplate | null;
  loading: boolean;
  saving: boolean;
  testing: boolean;
  sendingWelcome: boolean;
  sendingScheduled: boolean;
  previewPlainText: boolean;
  error: string;
  notice: string;
}

const TOKEN_HELP = [
  '{{first_name}}', '{{last_name}}', '{{recipient_name}}', '{{admins}}',
  '{{competition_name}}', '{{competition_start_date}}', '{{competition_end_date}}',
  '{{department_name}}', '{{participating_departments}}',
  '{{login_url}}', '{{action_url}}', '{{action_label}}',
  '{{actor_name}}', '{{title}}', '{{body}}',
  '{{contact_email}}', '{{frontend_host}}', '{{division_name}}'
];

class EmailTemplatesPage extends React.Component<LProps<Props>, LocalState> {
  public state: LocalState = {
    templates: [],
    selectedKey: '',
    draft: null,
    loading: true,
    saving: false,
    testing: false,
    sendingWelcome: false,
    sendingScheduled: false,
    previewPlainText: false,
    error: '',
    notice: ''
  };

  private bodyField: HTMLTextAreaElement | null = null;

  public componentDidMount() {
    this.loadTemplates();
  }

  private authHeaders = () => {
    const { token } = this.props;
    return {
      'Authorization': token,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  private loadTemplates = async () => {
    const { apiBase, competitionName, canManage } = this.props;
    if (!canManage) {
      this.setState({ loading: false, error: 'Admin access required.' });
      return;
    }
    this.setState({ loading: true, error: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${competitionName}/email_templates`,
        { headers: this.authHeaders() }
      );
      if (!response.ok) {
        this.setState({ loading: false, error: `Failed to load templates (${response.status}).` });
        return;
      }
      const data = await response.json();
      const templates: EmailTemplate[] = data.templates || [];
      const selectedKey = templates.length ? templates[0].templateKey : '';
      this.setState({
        templates,
        selectedKey,
        draft: templates.length ? { ...templates[0] } : null,
        loading: false
      });
    } catch (_err) {
      this.setState({ loading: false, error: 'Network error loading templates.' });
    }
  }

  private selectTemplate = (key: string) => {
    const tmpl = this.state.templates.find((item) => item.templateKey === key);
    this.setState({
      selectedKey: key,
      draft: tmpl ? { ...tmpl } : null,
      notice: '',
      error: ''
    });
  }

  private insertToken = (token: string) => {
    const { draft } = this.state;
    if (!draft) {
      return;
    }
    const current = draft.bodyHtml || '';
    const field = this.bodyField;
    let next = `${current}${token}`;
    let cursor = current.length + token.length;
    if (field && typeof field.selectionStart === 'number') {
      const start = field.selectionStart;
      const end = field.selectionEnd;
      next = current.slice(0, start) + token + current.slice(end);
      cursor = start + token.length;
    }
    this.setState({ draft: { ...draft, bodyHtml: next } }, () => {
      if (this.bodyField) {
        this.bodyField.focus();
        this.bodyField.setSelectionRange(cursor, cursor);
      }
    });
  }

  private save = async () => {
    const { apiBase, competitionName } = this.props;
    const { draft } = this.state;
    if (!draft) {
      return;
    }
    const bodyHtml = draft.bodyHtml || '';
    const bodyText = htmlToPlainText(bodyHtml) || draft.bodyText || '';
    this.setState({ saving: true, error: '', notice: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${competitionName}/email_templates/${draft.templateKey}`,
        {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify({
            name: draft.name,
            subject: draft.subject,
            bodyHtml,
            bodyText,
            previewText: draft.previewText,
            enabled: draft.enabled
          })
        }
      );
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        this.setState({
          saving: false,
          error: `Save failed (${response.status})${detail ? `: ${detail.slice(0, 200)}` : '.'}`
        });
        return;
      }
      const data = await response.json();
      const updated = data.template as EmailTemplate;
      const templates = this.state.templates.map((item) =>
        item.templateKey === updated.templateKey ? updated : item
      );
      this.setState({
        templates,
        draft: { ...updated },
        saving: false,
        notice: 'Template saved.'
      });
    } catch (_err) {
      this.setState({ saving: false, error: 'Network error while saving.' });
    }
  }

  private testSend = async () => {
    const { apiBase, competitionName } = this.props;
    const { draft } = this.state;
    if (!draft) {
      return;
    }
    this.setState({ testing: true, error: '', notice: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${competitionName}/email_templates/${draft.templateKey}/test`,
        { method: 'POST', headers: this.authHeaders(), body: '{}' }
      );
      if (!response.ok) {
        this.setState({ testing: false, error: `Test send failed (${response.status}).` });
        return;
      }
      this.setState({
        testing: false,
        notice: 'Test email sent to your account email using your real name and live login URL.'
      });
    } catch (_err) {
      this.setState({ testing: false, error: 'Network error during test send.' });
    }
  }

  private sendWelcomeNow = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ sendingWelcome: true, error: '', notice: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${competitionName}/preregistrations/send_welcome`,
        { method: 'POST', headers: this.authHeaders(), body: '{}' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        this.setState({ sendingWelcome: false, error: `Welcome send failed (${response.status}).` });
        return;
      }
      this.setState({
        sendingWelcome: false,
        notice: `Welcome send complete. Sent: ${data.sent || 0}`
      });
    } catch (_err) {
      this.setState({ sendingWelcome: false, error: 'Network error during welcome send.' });
    }
  }

  private sendScheduledNow = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ sendingScheduled: true, error: '', notice: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${competitionName}/preregistrations/send_scheduled`,
        { method: 'POST', headers: this.authHeaders(), body: '{}' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        this.setState({ sendingScheduled: false, error: `Scheduled send failed (${response.status}).` });
        return;
      }
      this.setState({
        sendingScheduled: false,
        notice: `Scheduled send complete. Welcome: ${data.welcomeSent || 0}`
      });
    } catch (_err) {
      this.setState({ sendingScheduled: false, error: 'Network error during scheduled send.' });
    }
  }

  public render() {
    const { t, canManage } = this.props;
    const {
      templates, draft, loading, saving, testing, sendingWelcome, sendingScheduled,
      previewPlainText, error, notice, selectedKey
    } = this.state;
    const meta = draft ? TEMPLATE_METADATA[draft.templateKey] : undefined;
    const previewHtml = draft ? applyTemplatePreviewTokens(draft.bodyHtml) : '';
    const previewSubject = draft ? applyTemplatePreviewTokens(draft.subject) : '';
    const previewText = draft ? applyTemplatePreviewTokens(htmlToPlainText(draft.bodyHtml || '')) : '';

    return (
      <div className="cc__page__column_content">
        <div className="cc__white_box" style={{ maxWidth: 960, margin: '0 auto' }}>
          <h2>{t('Email Templates')}</h2>
          <AdminGuideLink sectionId="communications" label={t('How automated emails work')} t={t} />
          <p className="cc__lighttext">
            {t('Edit the full email HTML below. Keep placeholders like {{first_name}} and {{login_url}} — they are filled in automatically for each recipient when the email is sent.')}
          </p>
          <Alert color="info">
            {t('Preview uses sample data (for example “Hi Alex”). Real sends use each person’s name, the live Challenge login URL, and the full participating-department list.')}
          </Alert>
          <p className="cc__lighttext" style={{ fontSize: 13 }}>
            {t('Available variables')}: {TOKEN_HELP.join(', ')}
          </p>

          {!canManage && <Alert color="danger" role="alert">{t('Admin or superadmin access is required.')}</Alert>}
          {error && <Alert color="danger" role="alert">{error}</Alert>}
          {notice && <Alert color="success" role="status">{notice}</Alert>}
          {loading && <p>{t('Loading…')}</p>}

          {!loading && canManage && (
            <div>
              <FormGroup>
                <Label for="tmpl-select">{t('Template')}</Label>
                <Input
                  id="tmpl-select"
                  type="select"
                  value={selectedKey}
                  onChange={(e) => this.selectTemplate(e.target.value)}
                >
                  {templates.map((tmpl) =>
                    <option key={tmpl.templateKey} value={tmpl.templateKey}>
                      {tmpl.name} ({tmpl.templateKey}){tmpl.enabled ? '' : ` — ${t('disabled')}`}
                    </option>
                  )}
                </Input>
              </FormGroup>

              {draft && (
                <div>
                  {meta &&
                    <Alert color="light" style={{ marginBottom: 16 }}>
                      <strong>{t('Purpose')}:</strong> {meta.purpose}<br />
                      <strong>{t('When it sends')}:</strong> {meta.sendsWhen}<br />
                      <strong>{t('Recipients')}:</strong> {meta.recipients}
                    </Alert>
                  }
                  <FormGroup>
                    <Label for="tmpl-name">{t('Name')}</Label>
                    <Input
                      id="tmpl-name"
                      value={draft.name}
                      onChange={(e) => this.setState({ draft: { ...draft, name: e.target.value } })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="tmpl-subject">{t('Subject')}</Label>
                    <Input
                      id="tmpl-subject"
                      value={draft.subject}
                      onChange={(e) => this.setState({ draft: { ...draft, subject: e.target.value } })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="tmpl-preview">{t('Preview text')}</Label>
                    <Input
                      id="tmpl-preview"
                      value={draft.previewText}
                      onChange={(e) => this.setState({ draft: { ...draft, previewText: e.target.value } })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="tmpl-insert">{t('Insert variable into email')}</Label>
                    <Input
                      id="tmpl-insert"
                      type="select"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) {
                          this.insertToken(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    >
                      <option value="">{t('Choose a variable…')}</option>
                      {INSERTABLE_TEMPLATE_TOKENS.map((item) =>
                        <option key={item.token} value={item.token}>{item.label}</option>
                      )}
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label for="tmpl-html">{t('Email content (HTML)')}</Label>
                    <Input
                      id="tmpl-html"
                      type="textarea"
                      rows={18}
                      value={draft.bodyHtml}
                      innerRef={(el: HTMLTextAreaElement | null) => { this.bodyField = el; }}
                      onChange={(e) => this.setState({ draft: { ...draft, bodyHtml: e.target.value } })}
                    />
                    <p className="cc__lighttext">
                      {t('Edit this HTML directly. Do not replace {{first_name}} with a fixed name — each recipient gets their own name when the email sends.')}
                    </p>
                  </FormGroup>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'center' }}>
                      <strong>{t('Email Preview')}</strong>
                      <Button color="link" size="sm" onClick={() => this.setState({ previewPlainText: !previewPlainText })}>
                        {previewPlainText ? t('HTML preview') : t('Plain text')}
                      </Button>
                    </div>
                    <div className="cc__white_box" style={{ padding: 16, border: '1px solid #dee2e6' }}>
                      {!previewPlainText &&
                        <React.Fragment>
                          <div style={{ marginBottom: 8 }}><strong>{previewSubject}</strong></div>
                          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                        </React.Fragment>
                      }
                      {previewPlainText &&
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{previewText}</pre>
                      }
                    </div>
                  </div>
                  <FormGroup check={true} style={{ marginBottom: 16 }}>
                    <CustomInput
                      type="checkbox"
                      id="tmpl-enabled"
                      checked={draft.enabled}
                      onChange={(e) => this.setState({ draft: { ...draft, enabled: e.target.checked } })}
                      label={t('Enabled')}
                    />
                  </FormGroup>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <Button color="primary" disabled={saving} onClick={this.save}>
                      {saving ? t('Saving…') : t('Save template')}
                    </Button>
                    <Button color="secondary" disabled={testing} onClick={this.testSend}>
                      {testing ? t('Sending…') : t('Send test to me')}
                    </Button>
                    <Button color="warning" disabled={sendingWelcome} onClick={this.sendWelcomeNow}>
                      {sendingWelcome ? t('Sending…') : t('Send due welcome emails')}
                    </Button>
                    <Button color="warning" disabled={sendingScheduled} onClick={this.sendScheduledNow}>
                      {sendingScheduled ? t('Sending…') : t('Send all due scheduled emails')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  canManage: selectors.canViewAdminTools(state)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  connect(mapStateToProps)
)(EmailTemplatesPage));
